import React, { useEffect, useState } from "react";
import { Calendar as MapPin, ExternalLink } from "lucide-react";
import { supabase } from "../supabaseClient";

export default function EventCalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    async function fetchEvents() {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .order("date", { ascending: true });

      if (!error) setEvents(data);
      setLoading(false);
    }
    fetchEvents();
  }, []);

  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#FFFFFF",
      fontFamily: "'Inter', sans-serif",
      padding: "1.5rem",
      paddingBottom: "100px",
    },
    header: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: "2.5rem",
      marginTop: "1.5rem",
    },
    pageTitle: {
      fontSize: "1.8rem",
      fontWeight: "900",
      margin: 0,
      background: "linear-gradient(90deg, #12873B 0%, #2D302D 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    eventCard: (isExpanded) => ({
      backgroundColor: "#F8FAFB",
      borderRadius: "28px",
      padding: "1.2rem",
      marginBottom: "1.2rem",
      border: isExpanded ? "2.5px solid #12873B" : "1px solid #E2E8F0",
      transition: "all 0.3s ease",
      cursor: "pointer",
      overflow: "hidden", // Prevents children from spilling out
    }),
    dateBadge: {
      backgroundColor: "#D1EAD9",
      color: "#12873B",
      padding: "0.6rem",
      borderRadius: "16px",
      textAlign: "center",
      minWidth: "55px",
      fontWeight: "800",
      display: "flex",
      flexDirection: "column",
      fontSize: "0.75rem",
    },
    pointsBadge: {
      backgroundColor: "#12873B",
      color: "#FFFFFF",
      padding: "6px 14px",
      borderRadius: "12px",
      fontSize: "0.85rem",
      fontWeight: "900",
      marginLeft: "auto", 
      boxShadow: "0 4px 10px rgba(18, 135, 59, 0.15)",
    },
    activityTitle: {
      fontSize: "1.05rem",
      fontWeight: "800",
      margin: "0 0 4px 0",
      color: "#2D302D",
    },
    signupBtn: {
      display: "flex", // Changed from inline-flex
      alignItems: "center",
      gap: "10px",
      background: "linear-gradient(90deg, #12873B 0%, #2D302D 100%)",
      color: "white",
      padding: "1.1rem",
      borderRadius: "18px",
      textDecoration: "none",
      fontWeight: "800",
      marginTop: "1.5rem",
      width: "100%",
      justifyContent: "center",
      boxSizing: "border-box", // CRITICAL FIX: Keeps padding inside the 100% width
      fontSize: "1rem",
      boxShadow: "0 8px 15px rgba(18, 135, 59, 0.2)",
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return {
      day: d.getDate(),
      month: d.toLocaleString('default', { month: 'short' }).toUpperCase()
    };
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.pageTitle}>Events Calendar</h1>
      </header>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: "5rem", color: "#12873B", fontWeight: "600" }}>Loading...</div>
      ) : (
        events.map((event) => {
          const { day, month } = formatDate(event.date);
          const isExpanded = expandedId === event.id;

          return (
            <div 
              key={event.id} 
              style={styles.eventCard(isExpanded)}
              onClick={() => setExpandedId(isExpanded ? null : event.id)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <div style={styles.dateBadge}>
                  <span>{month}</span>
                  <span style={{ fontSize: "1.3rem" }}>{day}</span>
                </div>

                <div style={{ flex: 1 }}>
                  <h4 style={styles.activityTitle}>{event.title}</h4>
                  <div style={{ opacity: 0.7, fontSize: "0.8rem", fontWeight: "600" }}>
                    <span>{event.association_name}</span>
                  </div>
                </div>
                
                <div style={styles.pointsBadge}>
                  {event.points_reward} PTS
                </div>
              </div>

              {isExpanded && (
                <div style={{ marginTop: "1.5rem", borderTop: "1px solid #E2E8F0", paddingTop: "1.2rem" }}>
                  <p style={{ fontSize: "0.95rem", color: "#4A5568", lineHeight: "1.6", margin: "0 0 1.2rem 0" }}>
                    {event.description}
                  </p>
                  
                  {event.location && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.9rem", color: "#12873B", fontWeight: "700" }}>
                      <MapPin size={18} />
                      {event.location}
                    </div>
                  )}

                  {/* Centered and Corrected Button */}
                  <a 
                    href={event.signup_url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={styles.signupBtn}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span>Join Action</span>
                    <ExternalLink size={18} />
                  </a>
                </div>
              )}
            </div>
          )
        })
      )}
    </div>
  );
}